import React from 'react';

const NOTICE_KEYS = new Set([
  'importantNotice',
  'acceptanceNote',
  'policyNote',
  'withdrawalInfo',
]);

const PARAGRAPH_KEYS = new Set([
  'content',
  'note',
  'disclaimer',
  'additionalInfo',
  'description',
  'maximumLiability',
  'holdPolicy',
  'settlementTimeline',
  'refundObligation',
  'registeredOffice',
  'brand',
  'name',
  'importantNote',
  'platformLiability',
  'partnerObligation',
  'platformOverride',
  'cancellationWindow',
  'howToCancel',
  'refundTimeline',
]);

const HEADING_KEYS = new Set(['heading', 'title']);

const NESTED_ARRAY_KEYS = new Set(['sections', 'subsections', 'subheadings']);

const humanizeKey = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase())
    .trim();

const headingClass = (depth) => {
  if (depth <= 0) return 'text-2xl font-bold mb-2 text-gray-900';
  if (depth === 1) return 'text-xl font-semibold mb-2 text-gray-900';
  if (depth === 2) return 'text-lg font-semibold mb-2 text-gray-900';
  return 'text-base font-medium mb-2 text-gray-900';
};

const DataTypesTable = ({ items }) => (
  <div className="overflow-x-auto mb-4">
    <table className="min-w-full text-sm border border-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {Object.keys(items[0])
            .filter((key) => key !== 'id')
            .map((key) => (
              <th key={key} className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                {humanizeKey(key)}
              </th>
            ))}
        </tr>
      </thead>
      <tbody>
        {items.map((row, rowIndex) => (
          <tr key={row.id || rowIndex} className="border-b last:border-b-0">
            {Object.entries(row)
              .filter(([key]) => key !== 'id')
              .map(([key, value]) => (
                <td key={key} className="px-3 py-2 align-top text-gray-800">
                  {Array.isArray(value) ? value.join(', ') : String(value)}
                </td>
              ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PreviewValue = ({ fieldKey, value, depth = 0 }) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    if (fieldKey === 'title' && depth === 0) {
      return <h1 className={headingClass(0)}>{value}</h1>;
    }

    if (HEADING_KEYS.has(fieldKey)) {
      return <div className={headingClass(depth)}>{value}</div>;
    }

    if (NOTICE_KEYS.has(fieldKey)) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 text-sm text-gray-800">
          {value}
        </div>
      );
    }

    if (PARAGRAPH_KEYS.has(fieldKey)) {
      return <p className="mb-2 text-gray-800 leading-relaxed">{value}</p>;
    }

    return (
      <p className="mb-2 text-gray-800 leading-relaxed">
        {fieldKey && fieldKey !== 'content' && (
          <span className="font-medium text-gray-700">{humanizeKey(fieldKey)}: </span>
        )}
        {value}
      </p>
    );
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return (
      <p className="mb-2 text-gray-800">
        <span className="font-medium text-gray-700">{humanizeKey(fieldKey)}: </span>
        {String(value)}
      </p>
    );
  }

  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return (
      <div className="mb-3">
        {fieldKey && !NESTED_ARRAY_KEYS.has(fieldKey) && fieldKey !== 'points' && (
          <p className="font-medium text-gray-700 mb-1">{humanizeKey(fieldKey)}</p>
        )}
        <ul className="list-disc ml-6 space-y-1 text-gray-800">
          {value.map((item, index) => (
            <li key={index} className="leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
    if (value[0].dataType && (value[0].examples || value[0].purpose)) {
      return (
        <div className="mb-4">
          {fieldKey && <p className="font-medium text-gray-700 mb-2">{humanizeKey(fieldKey)}</p>}
          <DataTypesTable items={value} />
        </div>
      );
    }

    return (
      <div className="mb-4">
        {fieldKey && !NESTED_ARRAY_KEYS.has(fieldKey) && (
          <p className="font-medium text-gray-700 mb-2">{humanizeKey(fieldKey)}</p>
        )}
        <div className={NESTED_ARRAY_KEYS.has(fieldKey) ? 'space-y-5' : 'space-y-3'}>
          {value.map((item, index) => (
            <div
              key={item.id || `${fieldKey}-${index}`}
              className={NESTED_ARRAY_KEYS.has(fieldKey) ? 'border-l-2 border-blue-200 pl-4' : 'ml-2'}
            >
              <PreviewObject value={item} depth={depth + 1} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return (
      <div className="mb-3">
        {fieldKey && depth > 0 && (
          <p className="font-medium text-gray-700 mb-2">{humanizeKey(fieldKey)}</p>
        )}
        <div className={depth > 0 ? 'ml-2' : ''}>
          <PreviewObject value={value} depth={depth + 1} />
        </div>
      </div>
    );
  }

  return null;
};

const PreviewObject = ({ value, depth = 0 }) => {
  const priority = { title: 0, heading: 0, content: 1, company: 2, effectiveDate: 2, lastUpdated: 2 };

  const entries = Object.entries(value)
    .filter(([key, entryValue]) => key !== 'id' && entryValue != null && entryValue !== '')
    .sort((a, b) => (priority[a[0]] ?? 10) - (priority[b[0]] ?? 10));

  return (
    <>
      {entries.map(([key, entryValue]) => (
        <PreviewValue key={key} fieldKey={key} value={entryValue} depth={depth} />
      ))}
    </>
  );
};

const DocumentPreview = ({ document }) => {
  if (!document || typeof document !== 'object') {
    return <p className="text-gray-500 text-sm">No document to preview.</p>;
  }

  return (
    <div className="prose prose-sm max-w-none overflow-y-auto max-h-[640px] pr-2">
      <PreviewObject value={document} depth={0} />
    </div>
  );
};

export default DocumentPreview;
